/**
 * Scatter Tank — Chip damage specialist. Volume over precision.
 * Hard to dodge entirely, but each hit is weak.
 */

import type { DeferredSpawn } from '../types';
import { registerWeapon, lookupWeapon, createProjectile } from '../weapons';
import { random } from '../terrain/rng';
import { registerTank } from './registry';
import { SMALL_CRATER } from '../terrain/crater-shapes';

// ─── Sub-weapons — tiny fragments for Scatter's multi-projectile shots ───────

const SCATTER_PELLET = registerWeapon({
  id: 'scatter_pellet',
  name: 'Pellet',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 4,
  projectileSpeed: 0.55,
  color: 0xff8866,
  gravity: 0.18,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 3,            // S1 nerf: was 5
  blastRadius: 55,
  knockbackMult: 0.07,
  knockbackRadius: 1.2,
  shakeIntensity: 5.0,
  particleScale: 0.4,
  craterShape: SMALL_CRATER,
  knockbackStacking: { windowFrames: 30, diminishingFactor: 0.8 },
  // Per-frame micro-trail — makes pellets visible against backgrounds
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 2 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, 0xff8866,
        0.1, 0.5, 0.04, 0.08, 0.12, 0.22, 0.02, 0.95, true, true, 0.5,
      ] });
    }
    return null;
  },
});

registerWeapon({
  id: 'scatter_bomblet',
  name: 'Bomblet',
  description: '',
  tier: 'SUB',
  rarity: 'common',
  projectileRadius: 6,
  projectileSpeed: 0.55,
  color: 0xff8800,
  gravity: 0.20,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 6,
  blastRadius: 65,
  knockbackMult: 0.075,
  knockbackRadius: 1.2,
  shakeIntensity: 4.5,
  particleScale: 0.5,
  craterShape: SMALL_CRATER,
  knockbackStacking: { windowFrames: 30, diminishingFactor: 0.8 },
  // Slightly larger trail than pellets — bomblets are bigger threats
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 3 === 0) {
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x, proj.y, 0xff8800,
        0.2, 0.8, 0.06, 0.1, 0.15, 0.3, 0.03, 0.94, true, true, 0.5,
      ] });
    }
    return null;
  },
});

// ─── Shot 1: Pellet Spread — Buckshot pattern, 5 pellets in tight cone ───────

const SCATTER_SHOT1 = registerWeapon({
  id: 'scatter_shot1',
  name: 'Pellet Spread',
  description: 'Shotgun spray. 5 tiny pellets in a tight cone. Chip damage adds up.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 6,
  projectileSpeed: 0.55,
  color: 0xff8866,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 0,
  blastRadius: 20,
  knockbackMult: 0.01,
  knockbackRadius: 0.5,
  shakeIntensity: 5.0,
  particleScale: 0.3,
  craterShape: SMALL_CRATER,
  tracerMode: 'cone',
  tracerConeConfig: { subWeaponId: 'scatter_pellet', count: 5, spread: 0.18 },
  edgeguard: { chargeFrames: 20, threshold: 25 },
  muzzleEffect: 'spawnScatterBuckshot',
  onTick(proj, state, fx) {
    // Immediately burst into pellets on first frame (Buckshot pattern)
    if (!proj.behaviorState.fired) {
      proj.behaviorState.fired = 1;
      const fragWeapon = lookupWeapon('scatter_pellet');
      const baseAngle = Math.atan2(proj.vy, proj.vx);
      const speed = Math.sqrt(proj.vx * proj.vx + proj.vy * proj.vy);
      const spawns: DeferredSpawn[] = [];
      // Origination burst — fan of sparks at split point
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xff8866, 6] });
      for (let i = 0; i < 5; i++) {
        const angle = baseAngle + (i - 2) * 0.09;
        const fragSpeed = speed * (0.9 + random() * 0.2);
        spawns.push({
          spawnFrame: state.frameCount,
          spawned: false,
          projectile: createProjectile(
            fragWeapon, proj.x, proj.y,
            Math.cos(angle) * fragSpeed,
            Math.sin(angle) * fragSpeed,
            proj.owner, state.frameCount, proj.generation + 1,
          ),
        });
      }
      return { consume: true, spawns };
    }
    return null;
  },
});

// ─── Shot 2: Cluster Bomb — Airburst pattern, splits at apex into 7 bomblets ─

const SCATTER_SHOT2 = registerWeapon({
  id: 'scatter_shot2',
  name: 'Cluster Bomb',
  description: 'Lands on terrain, then splits into 7 bomblets radiating outward.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 8,
  projectileSpeed: 0.50,
  color: 0xff6644,
  gravity: 0.15,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 0,
  blastRadius: 20,
  knockbackMult: 0.01,
  knockbackRadius: 0.5,
  shakeIntensity: 5.0,
  particleScale: 0.3,
  craterShape: SMALL_CRATER,
  edgeguard: { chargeFrames: 20, threshold: 25 },
  // Jittery orange sparks — unstable bomb feel
  onTick(proj, _state, fx) {
    proj.behaviorState.t = (proj.behaviorState.t || 0) + 1;
    if (proj.behaviorState.t % 3 === 0) {
      // Random offset gives it that "about to blow" instability
      const jitterX = (Math.random() - 0.5) * 14;
      const jitterY = (Math.random() - 0.5) * 14;
      fx.push({ type: 'particles', method: 'spawnParticle', args: [
        proj.x + jitterX, proj.y + jitterY, 0xff8800,
        0.3, 1.0, 0.04, 0.08, 0.12, 0.25, 0.02, 0.93, true, true, 0.5,
      ] });
      // Occasional darker spark for contrast (Sakurai: mix dark elements)
      if (Math.random() < 0.3) {
        fx.push({ type: 'particles', method: 'spawnParticle', args: [
          proj.x + jitterX * 0.5, proj.y + jitterY * 0.5, 0x663300,
          0.1, 0.4, 0.06, 0.1, 0.15, 0.3, 0.01, 0.95, false, false, 0.4,
        ] });
      }
    }
    return null;
  },
  onImpact(proj, x, y, _hitType, _hitIndex, state, _fx) {
    // On terrain/tank hit: split into 7 bomblets radiating outward
    // Spawn above impact point so bomblets don't immediately re-hit terrain
    const spawnY = y - 30;
    const childWeapon = lookupWeapon('scatter_bomblet');
    const spawns: DeferredSpawn[] = [];
    for (let i = 0; i < 7; i++) {
      const angle = (i / 7) * Math.PI * 2;
      const hSpread = Math.cos(angle) * (2.0 + random() * 1.0);
      const vSpread = -2.5 - random() * 1.5; // pop upward, then gravity brings them back down
      spawns.push({
        spawnFrame: state.frameCount + 2,
        spawned: false,
        projectile: createProjectile(
          childWeapon, x, spawnY,
          hSpread,
          vSpread,
          proj.owner, state.frameCount, proj.generation + 1,
        ),
      });
    }
    return spawns;
  },
});

registerTank({
  id: 'scatter',
  name: 'Scatter',
  description: 'Chip damage specialist. Volume over precision.',
  disabled: false,
  bonus: true,
  visual: { bodyColor: 0xEE6600, glowColor: 0xFF8800, bodyRadius: 32 },
  ecbRadius: 25,
  shots: [
    { tier: 1, weapon: SCATTER_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Pellet Spread', tierColor: 0xff8866 },
    { tier: 2, weapon: SCATTER_SHOT2, momentumCost: 0, hitlagBonus: 0, label: 'Cluster Bomb', tierColor: 0xff6644 },
  ],
});

// Suppress unused variable warning — SCATTER_PELLET is registered by side-effect
void SCATTER_PELLET;
